/**
 * Model class for Company validation
 */

import {required} from 'vuelidate/lib/validators'
import Api from '@/providers/Api'
import * as _ from 'lodash'
import Vue from 'vue'

const statusOptions = {
    ENABLED: 'Enabled',
    DISABLED: 'Disabled'
}
const taxTypeOptions = {
    EIN: 'EIN',
    SSN: 'SSN'
}
const cobidTypeOptions = {
    MBE: 'MBE',
    WBE: 'WBE',
    ESB: 'ESB',
    SDV: 'SDV',
    DBE: 'DBE'
}
const altLicenseOptions = [
    {value: 'ABN', text: 'Associated Business Name'},
    {value: 'DLP', text: 'Domestic Registered Limited Liability Partnership'},
    {value: 'DBC', text: 'Domestic Business Corporation'}
]

function formatCCBNumber(model) {
    let value = model['ccb_number']
    if ((value !== undefined) && (typeof (value) === 'string')) {
        value = value.replace(/\D/g, '')//Remove non-numeric
    }
    return value
}

function formatAddress(company) {
  return (
    (company.address_1 ? company.address_1 : "") +
    (company.address_2 ? ", " + company.address_2 : "") +
    (company.city ? "; " + company.city : "") +
    (company.state ? ", " + company.state : "") +
    (company.zip ? " " + company.zip : "")
  );
}

//Custom vuelidate validators
async function required_if_cobid_certified(value) {        //  do NOT convert to (value)=> format
    let is_cobid = this.$v.form.cobid_certified?.$model
    if (is_cobid) {
        return !!value
    }
    return true
}

async function required_if_alt_license(value) {
    return this.$v.form.alt_license?.$model ? !!value : true
}

async function at_least_one_if_cobid_certified(value) {
    let is_cobid = this.$v.form.cobid_certified?.$model
    if (is_cobid) {
        return Array.isArray(value) && value.length > 0
    }
    return true
}

export default {
    // helper method to populate Company options for other models
    async loadCompanyOptions(modelField) {
        const companies = await Api.getAllItems(null, 'company')
        modelField.options = _.map(companies, (company) => {
            return {
                value: company.id, text: company.name
            }
        })
    },
    async getCompanyName(id) {
        const company = await Api.getItem({}, 'company', id)
        return company.name
    },
    getTaxTypeLabel(value) {
        return taxTypeOptions[value]
    },
    formatCCBNumber: formatCCBNumber,
    formatAddress: formatAddress,
    fields: {
        id: {
            type: Number,
            showIf(model) {
                return false
            },
        },
        name: {
            label: 'Company Name',
            type: 'text',
            helpTag: 'company-name',
            validations: {
                required,
                name_unique: async function (value) {        //  do NOT convert to (value)=> format
                    if (value) {
                        let filters = {
                            name: {field: 'name', value: value}
                        }
                        let company_id = this.$v.form['id'] ? this.$v.form['id'].$model : null
                        if (company_id) {
                            filters.id = {field: 'company.id', value: company_id, negate: true}
                        }
                        return await this.$api.getItems(this, 'company', filters, null, [])
                            .then((companies) => {
                                let exists = false
                                if (companies.length) {
                                    companies.forEach((company) => {
                                        if (company.name && company.name.toLowerCase() === value.toLowerCase()) {
                                            exists = true
                                        }
                                    })
                                }
                                return !exists
                            })
                    }
                    return true
                }
            },
            invalidFeedback: {
                required: 'The company name is required',
                name_unique: 'This company name already exists',
            }
        },
        alt_license: {
            label: 'Check here if you don\'t have a CCB License',
            type: 'check',
            onChange: function (model) {
                this.$v.from.$touch()
            }
        },
        ccb_number: {
            label: 'CCB License #',
            type: 'text',
            disableIf(model) {
                return model.alt_license
            },
            onChange(model) {
                Vue.nextTick(() => {
                    model['ccb_number'] = formatCCBNumber(model)
                })
            },
            validations: {
                required: async function (value) {
                    return this.$v.form.alt_license?.$model || value
                },
                ccb_unique: async function (value) {        //  do NOT convert to (value)=> format
                    if (value) {
                        let filters = {
                            ccb_number: {field: 'ccb_number', value: value}
                        }
                        let company_id = this.$v.form['id'] ? this.$v.form['id'].$model : null
                        if (company_id) {
                            filters.id = {field: 'company.id', value: company_id, negate: true}
                        }
                        return await this.$api.getItems(this, 'company', filters, null, [])
                            .then((companies) => {
                                let exists = false
                                if (companies.length) {
                                    companies.forEach((company) => {
                                        if (company.ccb_number && company.ccb_number === value) {
                                            exists = true
                                        }
                                    })
                                }
                                return !exists
                            })
                    }
                    return true
                }
            },
            invalidFeedback: {
                required_if_ccb: 'This is required',
                ccb_unique: 'Another company with this CCB number already exists'
            }
        },
        alt_license_type: {
            label: 'Alt Oregon License Type',
            type: 'select',
            options: altLicenseOptions,
            disableIf(model) {
                return !model.alt_license
            },
            validations: {
                required: required_if_alt_license,
            },
            invalidFeedback: {
                required_if_alt_license: 'This field is required if no CCB License if provided',
            },
        },
        alt_license_number: {
            label: 'Alt License #',
            type: 'text',
            disableIf(model) {
                return !model.alt_license
            },
            validations: {
                required: required_if_alt_license,
                alt_license_unique: async function (value) {
                    if (value) {
                        let filters = {
                            alt_license_type: {field: 'alt_license_type', value: this.$v.form.alt_license_type.$model},
                            alt_license_number: {field: 'alt_license_number', value: value}
                        }
                        let company_id = this.$v.form['id'] ? this.$v.form['id'].$model : null
                        if (company_id) {
                            filters.id = {field: 'company.id', value: company_id, negate: true}
                        }
                        return await this.$api.getItems(this, 'company', filters, null, [])
                            .then((companies) => {
                                let exists = false
                                if (companies.length) {
                                    companies.forEach((company) => {
                                        if (company.alt_license_type && company.alt_license_type === this.$v.form.alt_license_type.$model && company.alt_license_number && company.alt_license_number === value) {
                                            exists = true
                                        }
                                    })
                                }
                                return !exists
                            })
                    }
                    return true
                }
            },
            invalidFeedback: {
                required_if_alt_license: 'This field is required if no CCB License if provided',
                alt_license_unique: 'Another company with this license type and number already exists'
            },
        },
        address_1: {
            label: 'Address',
            type: 'text',
            validations: {
                required,
            }
        },
        address_2: {
            label: 'Address 2',
            type: 'text',
            helpTag: 'company-address-2',
        },
        mailing_address: {
            label: 'Mailing Address',
            type: 'textarea',
            validations: {
                required,
            }
        },
        city: {
            label: 'City',
            type: 'text',
            validations: {
                required,
            }
        },
        state: {
            label: 'State',
            type: 'state',
            validations: {
                required,
            }
        },
        zip: {
            label: 'Zip',
            type: 'text',
            validations: {
                required,
            }
        },
        url: {
            label: 'URL',
            type: 'text',
            helpTag: 'company-url',
        },
        status: {
            label: 'Status',
            type: 'select',
            options: statusOptions
        },
        rental: {
            label: "Rental Company",
            type: "check"
        },
        hide_equipment: {
            label: "Hide Equipment",
            type: "check",
            showIf(model) {
                return model?.rental;
            },
            getDescription(model) {
                return 'By default, your equipment and company details are visible to all users on The Yard. To keep this information private from other companies, select this Hide Equipment checkbox.'
            },
            onChange: function(model) {
                //Force validation refresh
                this.$v?.from?.$touch();
            }
        },
        cobid_certified: {
            label: 'COBID Certified',
            type: 'check',
            onChange: function (model) { //Force validation refresh
                this.$v.from.$touch()
            }
        },
        cobid_number: {
            label: 'COBID #',
            type: 'text',
            showIf(model) {
                return model.cobid_certified;
            },
            validations: {
                required: required_if_cobid_certified,
            },
            invalid_feedback: {
                required_if_cobid_certified: 'This field is required if COBID certified',
            },
            getDescription(model) {
                return model.cobid_certified ? 'COBID # is required if COBID certified' : ''
            }
        },
        cobid_types: {
            label: 'COBID Type(s)',
            type: 'checks',
            options: cobidTypeOptions,
            showIf(model) {
                return model.cobid_certified;
            },
            validations: {
                required: at_least_one_if_cobid_certified,
            },
            invalid_feedback: {
                at_least_one_if_cobid_certified: 'Select at least one or more if COBID certified'
            },
            getDescription(model) {
                return model.cobid_certified ? 'Select at least one or more if COBID certified' : ''
            }
        },
        companyAdminUser: {
            label: 'Company Admin'
        }
    },
    getStatusLabel(value) {
        return statusOptions[value]
    },
    getAltLicenseTypeLabel(value) {
        return altLicenseOptions[value]
    }
}
